/**
 * Login Page - HYB MASTER SHOP
 * Web management dashboard login with JWT authentication
 */

import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Loader2, User, Lock } from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import logoUrl from '../assets/logo.svg';
import { useIsMobileOrTablet } from '../hooks/useMediaQuery';
import { cn } from '../utils/responsive';

interface LoginForm {
  username: string;
  password: string;
}

export function LoginPage() {
  const { isAuthenticated, login, isLoading } = useAuth();
  const { t } = useTranslation();
  const isMobileOrTablet = useIsMobileOrTablet();
  const [showPassword, setShowPassword] = useState(false);
  const [loginAttempting, setLoginAttempting] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>();

  // Redirect if already authenticated
  if (isAuthenticated) {
    const from = location.state?.from?.pathname || '/';
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (data: LoginForm) => {
    setLoginAttempting(true);

    try {
      const result = await login(data.username.trim(), data.password);

      if (result.success) {
        // Check if user must change password on first login
        if (result.requirePasswordChange) {
          toast.success(t('auth.authSuccess'));
          // Redirect to force change password page
          navigate('/force-change-password', {
            state: { username: result.username },
            replace: true
          });
        } else {
          toast.success(t('auth.loginSuccess'));
        }
      } else {
        toast.error(result.error || t('auth.invalidCredentials'));
      }
    } catch (error) {
      toast.error(t('auth.loginError'));
    } finally {
      setLoginAttempting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-stone-50 to-stone-100 dark:from-gray-900 dark:to-gray-800">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-400 via-orange-400 to-orange-500 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4 lg:py-12 lg:px-8">
      <div className={cn(
        "w-full",
        isMobileOrTablet ? "max-w-sm" : "max-w-md"
      )}>
        {/* Login Card */}
        <div className={cn(
          "bg-white dark:bg-gray-800 rounded-2xl shadow-2xl",
          isMobileOrTablet ? "p-6" : "p-8"
        )}>
          {/* Header */}
          <div className={cn(
            "text-center",
            isMobileOrTablet ? "mb-6" : "mb-8"
          )}>
            <img
              src={logoUrl}
              alt="HYB Master Shop"
              className={cn(
                "mx-auto mb-4 w-auto",
                isMobileOrTablet ? "h-12" : "h-16"
              )}
            />
            <h2 className={cn(
              "font-bold text-stone-900 dark:text-gray-100 mb-2",
              isMobileOrTablet ? "text-xl" : "text-3xl"
            )}>
              Web Management Dashboard
            </h2>
            <p className={cn(
              "text-stone-600 dark:text-gray-400",
              isMobileOrTablet ? "text-sm" : "text-base"
            )}>
              {t('auth.loginToManage')}
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit(onSubmit)} className={cn(
            "space-y-5",
            !isMobileOrTablet && "space-y-6"
          )}>
            {/* Username Field */}
            <div>
              <label htmlFor="username" className={cn(
                "block font-medium text-stone-700 dark:text-gray-300 mb-2",
                isMobileOrTablet ? "text-xs" : "text-sm"
              )}>
                {t('auth.username')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-stone-400 dark:text-gray-500" />
                </div>
                <input
                  {...register('username', {
                    required: t('auth.pleaseEnterUsername'),
                    minLength: { value: 3, message: t('auth.usernameMinLength') }
                  })}
                  type="text"
                  className={cn(
                    "block w-full pl-10 pr-3 border border-stone-300 dark:border-gray-600 rounded-lg",
                    "text-stone-900 dark:text-gray-100 placeholder:text-stone-400 dark:placeholder:text-gray-500",
                    "bg-white dark:bg-gray-900",
                    "focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500 focus:border-yellow-500 dark:focus:border-orange-500",
                    "transition-all duration-200",
                    isMobileOrTablet ? "py-2.5 text-sm" : "py-3 text-base"
                  )}
                  placeholder={t('auth.enterUsername')}
                />
              </div>
              {errors.username && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{String(errors.username?.message || '')}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className={cn(
                "block font-medium text-stone-700 dark:text-gray-300 mb-2",
                isMobileOrTablet ? "text-xs" : "text-sm"
              )}>
                {t('auth.password')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-stone-400 dark:text-gray-500" />
                </div>
                <input
                  {...register('password', {
                    required: t('auth.pleaseEnterPassword'),
                    minLength: { value: 8, message: t('auth.passwordMinLength') }
                  })}
                  type={showPassword ? 'text' : 'password'}
                  className={cn(
                    "block w-full pl-10 pr-10 border border-stone-300 dark:border-gray-600 rounded-lg",
                    "text-stone-900 dark:text-gray-100 placeholder:text-stone-400 dark:placeholder:text-gray-500",
                    "bg-white dark:bg-gray-900",
                    "focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500 focus:border-yellow-500 dark:focus:border-orange-500",
                    "transition-all duration-200",
                    isMobileOrTablet ? "py-2.5 text-sm" : "py-3 text-base"
                  )}
                  placeholder={t('auth.enterPassword')}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center touch-target"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-stone-400 dark:text-gray-500 hover:text-stone-600 dark:hover:text-gray-400 transition-colors" />
                  ) : (
                    <Eye className="h-5 w-5 text-stone-400 dark:text-gray-500 hover:text-stone-600 dark:hover:text-gray-400 transition-colors" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{String(errors.password?.message || '')}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loginAttempting}
              className={cn(
                "w-full flex justify-center items-center",
                "px-4 border border-transparent rounded-lg shadow-sm",
                "text-white font-semibold",
                "bg-gradient-to-r from-yellow-400 to-orange-500",
                "hover:from-orange-500 hover:to-yellow-400 hover:shadow-accent",
                "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500",
                "transition-all duration-200",
                "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-sm",
                "touch-target",
                isMobileOrTablet ? "py-2.5 text-sm" : "py-3 text-base"
              )}
            >
              {loginAttempting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  {t('auth.loggingIn')}
                </>
              ) : (
                t('auth.login')
              )}
            </button>
          </form>

          {/* Footer */}
          <div className={cn(
            "text-center text-stone-500 dark:text-gray-500",
            isMobileOrTablet ? "mt-6 text-xs" : "mt-8 text-sm"
          )}>
            <p>
              © 2024 HYB MASTER SHOP - Web Management Dashboard
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
